import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';

const TARGET_MARGIN = 0.4;
const TARGET_COST_RATE = 1 - TARGET_MARGIN;

const BASE_URL = 'https://thornsdashboard.com';

const PLATFORMS = ['ebay', 'shopify', 'tiktok', 'whatnot'];

const getImageUrl = (image) => {
  if (!image) return null;

  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }

  if (image.startsWith('/')) {
    return `${BASE_URL}${image}`;
  }

  return `${BASE_URL}/uploads/${image}`;
};

const formatMoney = (value, decimals = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '$0.00';
  return `$${num.toFixed(decimals)}`;
};

const parseMoneyNumber = (value) => {
  const cleaned = String(value ?? '').replace(/[^0-9.]/g, '');
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : 0;
};

const parseSettingNum = (value) => {
  const num = parseFloat(String(value ?? ''));
  return Number.isFinite(num) ? num : 0;
};

const buildDefaultPurchaseName = () => {
  const now = new Date();

  return `Quick Buy ${now.toLocaleDateString()} ${now.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const toSettingsDraft = (data) => {
  return {
    ebay_payment_percent: data.ebay_payment_percent?.toString() ?? '',
    ebay_payment_flat: data.ebay_payment_flat?.toString() ?? '',
    shopify_payment_percent:
      data.shopify_payment_percent?.toString() ?? '',
    shopify_payment_flat:
      data.shopify_payment_flat?.toString() ?? '',
    tiktok_payment_percent:
      data.tiktok_payment_percent?.toString() ?? '',
    tiktok_payment_flat:
      data.tiktok_payment_flat?.toString() ?? '',
    whatnot_payment_percent:
      data.whatnot_payment_percent?.toString() ?? '',
    whatnot_payment_flat:
      data.whatnot_payment_flat?.toString() ?? '',

    ebay_fee_percent: data.ebay_fee_percent?.toString() ?? '',
    shopify_fee_percent:
      data.shopify_fee_percent?.toString() ?? '',
    tiktok_fee_percent:
      data.tiktok_fee_percent?.toString() ?? '',
    whatnot_fee_percent:
      data.whatnot_fee_percent?.toString() ?? '',

    box_cost: data.box_cost?.toString() ?? '',
    bubble_cost: data.bubble_cost?.toString() ?? '',
    tape_cost: data.tape_cost?.toString() ?? '',
    misc_packaging_cost:
      data.misc_packaging_cost?.toString() ?? '',
  };
};

export default function QuickBuy() {
  const [skus, setSkus] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [totalCost, setTotalCost] = useState('');
  const [shippingCost, setShippingCost] = useState('');
  const [notes, setNotes] = useState('');
  const [commitNoteDraft, setCommitNoteDraft] = useState('');
  const [search, setSearch] = useState('');

  const [quickAddSku, setQuickAddSku] = useState('');
  const [addingQuickSku, setAddingQuickSku] = useState(false);
  const [importingShopify, setImportingShopify] = useState(false);

  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');

  const [profitSettings, setProfitSettings] = useState(null);
  const [profitSettingsDraft, setProfitSettingsDraft] =
    useState(null);
  const [profitSettingsError, setProfitSettingsError] =
    useState('');
  const [
    showProfitSettingsEditor,
    setShowProfitSettingsEditor,
  ] = useState(false);
  const [
    savingProfitSettings,
    setSavingProfitSettings,
  ] = useState(false);

  const selectedArray = useMemo(
    () => Object.values(selectedItems),
    [selectedItems]
  );

  const totalQty = useMemo(() => {
    return selectedArray.reduce(
      (sum, item) => sum + Number(item.qty || 0),
      0
    );
  }, [selectedArray]);

  const totalCostNumber = useMemo(() => {
    return parseMoneyNumber(totalCost);
  }, [totalCost]);

  const shippingCostNumber = useMemo(() => {
    return parseMoneyNumber(shippingCost);
  }, [shippingCost]);

  const productsBySku = useMemo(() => {
    const map = {};

    skus.forEach((item) => {
      map[item.sku] = item;
    });

    return map;
  }, [skus]);

  const averageUnitCost = useMemo(() => {
    if (totalQty <= 0) return 0;
    return totalCostNumber / totalQty;
  }, [totalCostNumber, totalQty]);

  const getEffectiveSellPrice = useCallback(
    (sku) => {
      const selected = selectedItems[sku];
      const product = productsBySku[sku];

      const tempPrice = parseMoneyNumber(
        selected?.tempSellPrice
      );

      if (tempPrice > 0) return tempPrice;

      return Number(product?.price || 0);
    },
    [productsBySku, selectedItems]
  );

  const weightedCostBySku = useMemo(() => {
    const weightedItems = selectedArray.map((selected) => {
      const product = productsBySku[selected.sku];
      const normalSellPrice = Number(product?.price || 0);
      const tempSellPrice = parseMoneyNumber(
        selected.tempSellPrice
      );

      const sellPrice =
        tempSellPrice > 0
          ? tempSellPrice
          : normalSellPrice;

      const isTemporaryPrice =
        tempSellPrice > 0 &&
        tempSellPrice !== normalSellPrice;

      const targetUnitCost =
        sellPrice > 0
          ? sellPrice * TARGET_COST_RATE
          : 1;

      const weight = targetUnitCost * selected.qty;

      return {
        sku: selected.sku,
        qty: selected.qty,
        sellPrice,
        targetUnitCost,
        weight,
        isTemporaryPrice,
      };
    });

    const totalWeight = weightedItems.reduce(
      (sum, item) => sum + item.weight,
      0
    );

    const map = {};

    weightedItems.forEach((item) => {
      if (totalWeight <= 0 || totalCostNumber <= 0) {
        map[item.sku] = {
          sellPrice: item.sellPrice,
          targetUnitCost: item.targetUnitCost,
          weight: item.weight,
          assignedTotal: 0,
          unitCost: 0,
          isTemporaryPrice: item.isTemporaryPrice,
        };

        return;
      }

      const assignedTotal =
        totalCostNumber * (item.weight / totalWeight);

      const unitCost =
        assignedTotal / item.qty;

      map[item.sku] = {
        sellPrice: item.sellPrice,
        targetUnitCost: item.targetUnitCost,
        weight: item.weight,
        assignedTotal,
        unitCost,
        isTemporaryPrice: item.isTemporaryPrice,
      };
    });

    return map;
  }, [selectedArray, productsBySku, totalCostNumber]);

  const selectedPackagingProfiles = useMemo(() => {
    if (
      !Array.isArray(
        profitSettings?.packaging_profiles
      )
    ) {
      return [];
    }

    return profitSettings.packaging_profiles.filter(
      (profile) => !!profile.selected
    );
  }, [profitSettings]);

  const packagingCost = useMemo(() => {
    if (!profitSettings) return 0;

    return selectedPackagingProfiles.reduce(
      (sum, profile) =>
        sum + Number(profile.cost || 0),
      0
    );
  }, [profitSettings, selectedPackagingProfiles]);

  const calcPlatformNet = useCallback(
    ({ platform, salePrice, cogs }) => {
      if (!profitSettings) {
        return {
          platform,
          salePrice,
          cogs,
          shipping: shippingCostNumber,
          packaging: 0,
          marketplaceFee: 0,
          paymentFee: 0,
          totalCost: cogs,
          profit: 0,
          marginPercent: 0,
          roiPercent: 0,
        };
      }

      const marketplaceFee =
        salePrice *
        ((Number(
          profitSettings[
            `${platform}_fee_percent`
          ]
        ) || 0) /
          100);

      const paymentFee =
        salePrice *
          ((Number(
            profitSettings[
              `${platform}_payment_percent`
            ]
          ) || 0) /
            100) +
        (Number(
          profitSettings[
            `${platform}_payment_flat`
          ]
        ) || 0);

      const totalCostValue =
        cogs +
        shippingCostNumber +
        packagingCost +
        marketplaceFee +
        paymentFee;

      const profit =
        salePrice - totalCostValue;

      const marginPercent =
        salePrice > 0
          ? (profit / salePrice) * 100
          : 0;

      const roiPercent =
        cogs > 0
          ? (profit / cogs) * 100
          : 0;

      return {
        platform,
        salePrice,
        cogs,
        shipping: shippingCostNumber,
        packaging: packagingCost,
        marketplaceFee,
        paymentFee,
        totalCost: totalCostValue,
        profit,
        marginPercent,
        roiPercent,
      };
    },
    [
      profitSettings,
      shippingCostNumber,
      packagingCost,
    ]
  );

  const roiBySku = useMemo(() => {
    const map = {};

    selectedArray.forEach((item) => {
      const weighted =
        weightedCostBySku[item.sku];

      const salePrice = Number(
        weighted?.sellPrice || 0
      );

      const cogs = Number(
        weighted?.unitCost || 0
      );

      map[item.sku] = {};

      PLATFORMS.forEach((platform) => {
        map[item.sku][platform] =
          calcPlatformNet({
            platform,
            salePrice,
            cogs,
          });
      });
    });

    return map;
  }, [
    selectedArray,
    weightedCostBySku,
    calcPlatformNet,
  ]);

  const platformTotals = useMemo(() => {
    const totals = {};

    PLATFORMS.forEach((platform) => {
      const values = selectedArray
        .map(
          (item) =>
            roiBySku[item.sku]?.[platform]
        )
        .filter(Boolean);

      const totalSale = values.reduce(
        (sum, item) =>
          sum + Number(item.salePrice || 0),
        0
      );

      const totalCogs = values.reduce(
        (sum, item) =>
          sum + Number(item.cogs || 0),
        0
      );

      const totalFees = values.reduce(
        (sum, item) =>
          sum +
          Number(item.marketplaceFee || 0) +
          Number(item.paymentFee || 0) +
          Number(item.packaging || 0) +
          Number(item.shipping || 0),
        0
      );

      const totalProfit = values.reduce(
        (sum, item) =>
          sum + Number(item.profit || 0),
        0
      );

      totals[platform] = {
        totalSale,
        totalCogs,
        totalFees,
        totalProfit,
        marginPercent:
          totalSale > 0
            ? (totalProfit / totalSale) * 100
            : 0,
        roiPercent:
          totalCogs > 0
            ? (totalProfit / totalCogs) * 100
            : 0,
      };
    });

    return totals;
  }, [roiBySku, selectedArray]);

  const displaySkus = useMemo(() => {
    const query = search.trim().toLowerCase();

    let rows = [...skus];

    if (query) {
      rows = rows.filter((item) => {
        const label = String(
          item.label || ''
        ).toLowerCase();

        const name = String(
          item.name || ''
        ).toLowerCase();

        const sku = String(
          item.sku || ''
        ).toLowerCase();

        return (
          label.includes(query) ||
          name.includes(query) ||
          sku.includes(query)
        );
      });
    }

    if (showOnlySelected) {
      rows = rows.filter(
        (item) => !!selectedItems[item.sku]
      );
    }

    rows.sort((a, b) => {
      const pinnedA = Number(
        a.is_pinned || 0
      );

      const pinnedB = Number(
        b.is_pinned || 0
      );

      if (pinnedA !== pinnedB) {
        return pinnedB - pinnedA;
      }

      return (
        Number(a.sort_order || 0) -
        Number(b.sort_order || 0)
      );
    });

    return rows;
  }, [
    skus,
    search,
    selectedItems,
    showOnlySelected,
  ]);

  const loadSkus = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get(
        '/quick-batch/skus'
      );

      setSkus(response.data?.skus || []);
    } catch (err) {
      console.error(
        '[QuickBuy Web] loadSkus error:',
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error ||
          err.message ||
          'Failed to load Quick Buy SKUs.'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadProfitSettings = async () => {
    try {
      setProfitSettingsError('');

      const response = await api.get(
        '/profit-calculator/settings'
      );

      setProfitSettings(response.data);
      setProfitSettingsDraft(
        toSettingsDraft(response.data)
      );
    } catch (err) {
      console.error(
        '[QuickBuy Web] profit settings error:',
        err.response?.data || err.message
      );

      setProfitSettingsError(
        err.response?.data?.error ||
          err.message ||
          'Could not load profit calculator settings.'
      );
    }
  };

  useEffect(() => {
    loadSkus();
    loadProfitSettings();
  }, []);

  const addOrIncreaseSku = useCallback(
    (item) => {
      setSelectedItems((previous) => {
        const existing =
          previous[item.sku];

        return {
          ...previous,
          [item.sku]: {
            sku: item.sku,
            label:
              item.label ||
              item.name ||
              item.sku,
            name: item.name,
            qty: existing
              ? existing.qty + 1
              : 1,
            image: item.image || null,
            tempSellPrice:
              existing?.tempSellPrice || '',
          },
        };
      });
    },
    []
  );

  const removeSku = useCallback((sku) => {
    setSelectedItems((previous) => {
      const next = { ...previous };
      delete next[sku];
      return next;
    });
  }, []);

  const updateQty = useCallback(
    (sku, qty) => {
      setSelectedItems((previous) => {
        if (!previous[sku]) return previous;

        const nextQty = Math.max(
          0,
          Math.floor(Number(qty) || 0)
        );

        const next = { ...previous };

        if (nextQty <= 0) {
          delete next[sku];
        } else {
          next[sku] = {
            ...next[sku],
            qty: nextQty,
          };
        }

        return next;
      });
    },
    []
  );

  const updateTempSellPrice = useCallback(
    (sku, value) => {
      setSelectedItems((previous) => {
        if (!previous[sku]) return previous;

        return {
          ...previous,
          [sku]: {
            ...previous[sku],
            tempSellPrice: value,
          },
        };
      });
    },
    []
  );

  const resetTempSellPrice = useCallback(
    (sku) => {
      setSelectedItems((previous) => {
        if (!previous[sku]) return previous;

        return {
          ...previous,
          [sku]: {
            ...previous[sku],
            tempSellPrice: '',
          },
        };
      });
    },
    []
  );

  const updateProfitSettingField = (
    key,
    value
  ) => {
    setProfitSettingsDraft((previous) => ({
      ...(previous || {}),
      [key]: value,
    }));
  };

  const updatePackagingProfile = (
    id,
    field,
    value
  ) => {
    setProfitSettings((previous) => {
      if (!previous) return previous;

      return {
        ...previous,
        packaging_profiles: (
          previous.packaging_profiles || []
        ).map((profile) =>
          String(profile.id) === String(id)
            ? {
                ...profile,
                [field]: value,
              }
            : profile
        ),
      };
    });
  };

  const addPackagingProfile = () => {
    const newProfile = {
      id: Date.now().toString(),
      name: 'New Packaging',
      cost: '',
      selected: true,
    };

    setProfitSettings((previous) => {
      if (!previous) {
        return {
          packaging_profiles: [newProfile],
        };
      }

      return {
        ...previous,
        packaging_profiles: [
          ...(previous.packaging_profiles ||
            []),
          newProfile,
        ],
      };
    });
  };

  const saveProfitSettings = async () => {
    if (
      !profitSettings ||
      !profitSettingsDraft
    ) {
      alert(
        'Profit settings are not loaded yet.'
      );

      return;
    }

    try {
      setSavingProfitSettings(true);

      const payload = {
        ...profitSettingsDraft,

        box_cost: parseSettingNum(
          profitSettingsDraft.box_cost
        ),

        bubble_cost: parseSettingNum(
          profitSettingsDraft.bubble_cost
        ),

        tape_cost: parseSettingNum(
          profitSettingsDraft.tape_cost
        ),

        misc_packaging_cost:
          parseSettingNum(
            profitSettingsDraft.misc_packaging_cost
          ),

        ebay_fee_percent:
          parseSettingNum(
            profitSettingsDraft.ebay_fee_percent
          ),

        shopify_fee_percent:
          parseSettingNum(
            profitSettingsDraft.shopify_fee_percent
          ),

        tiktok_fee_percent:
          parseSettingNum(
            profitSettingsDraft.tiktok_fee_percent
          ),

        whatnot_fee_percent:
          parseSettingNum(
            profitSettingsDraft.whatnot_fee_percent
          ),

        ebay_payment_percent:
          parseSettingNum(
            profitSettingsDraft.ebay_payment_percent
          ),

        ebay_payment_flat:
          parseSettingNum(
            profitSettingsDraft.ebay_payment_flat
          ),

        shopify_payment_percent:
          parseSettingNum(
            profitSettingsDraft.shopify_payment_percent
          ),

        shopify_payment_flat:
          parseSettingNum(
            profitSettingsDraft.shopify_payment_flat
          ),

        tiktok_payment_percent:
          parseSettingNum(
            profitSettingsDraft.tiktok_payment_percent
          ),

        tiktok_payment_flat:
          parseSettingNum(
            profitSettingsDraft.tiktok_payment_flat
          ),

        whatnot_payment_percent:
          parseSettingNum(
            profitSettingsDraft.whatnot_payment_percent
          ),

        whatnot_payment_flat:
          parseSettingNum(
            profitSettingsDraft.whatnot_payment_flat
          ),

        packaging_profiles: (
          profitSettings.packaging_profiles ||
          []
        ).map((profile) => ({
          id: String(profile.id),
          name: String(
            profile.name || ''
          ),
          cost: parseSettingNum(
            profile.cost
          ),
          selected: !!profile.selected,
        })),
      };

      await api.put(
        '/profit-calculator/settings',
        payload
      );

      alert(
        'Profit calculator settings saved.'
      );

      await loadProfitSettings();

      setShowProfitSettingsEditor(false);
    } catch (err) {
      console.error(
        '[QuickBuy Web] save profit settings error:',
        err.response?.data || err.message
      );

      alert(
        err.response?.data?.error ||
          err.message ||
          'Failed to save profit calculator settings.'
      );
    } finally {
      setSavingProfitSettings(false);
    }
  };

  const settingsInput = (key, label) => (
    <div className="mb-2">
      <label className="form-label mb-1">
        {label}
      </label>

      <input
        className="form-control"
        value={
          profitSettingsDraft?.[key] ??
          ''
        }
        onChange={(event) =>
          updateProfitSettingField(
            key,
            event.target.value
          )
        }
        placeholder="0"
      />
    </div>
  );

  const addSkuToQuickBuy = async () => {
    const sku = quickAddSku.trim();

    if (!sku) {
      alert('Enter a SKU first.');
      return;
    }

    try {
      setAddingQuickSku(true);

      await api.post('/quick-batch/skus', {
        sku,
      });

      alert(
        `${sku} added to Quick Buy.`
      );

      setQuickAddSku('');
      await loadSkus();
    } catch (err) {
      console.error(
        '[QuickBuy Web] add SKU error:',
        err.response?.data || err.message
      );

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to add SKU to Quick Buy.'
      );
    } finally {
      setAddingQuickSku(false);
    }
  };

  const handleImportFromShopify = async () => {
    try {
      setImportingShopify(true);
      setError('');

      const response = await api.post(
        '/shopify/import'
      );

      alert(
        response.data?.message ||
          'Shopify import completed successfully.'
      );

      await loadSkus();
    } catch (err) {
      console.error(
        '[QuickBuy Web] Shopify import error:',
        err.response?.data || err.message
      );

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to import from Shopify.'
      );
    } finally {
      setImportingShopify(false);
    }
  };

  const togglePinnedSku = async (item) => {
    try {
      const nextPinned =
        !Number(item.is_pinned || 0);

      await api.put(
        `/quick-batch/skus/${encodeURIComponent(
          item.sku
        )}/pin`,
        {
          is_pinned: nextPinned,
        }
      );

      setSkus((previous) =>
        previous.map((row) =>
          row.sku === item.sku
            ? {
                ...row,
                is_pinned: nextPinned
                  ? 1
                  : 0,
              }
            : row
        )
      );
    } catch (err) {
      console.error(
        '[QuickBuy Web] toggle pin error:',
        err.response?.data || err.message
      );

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to update pinned status.'
      );
    }
  };

  const clearCart = () => {
    setSelectedItems({});
    setTotalCost('');
    setShippingCost('');
    setNotes('');
    setCommitNoteDraft('');
    setSearch('');
    setShowOnlySelected(false);
  };

  const openCommit = async () => {
    if (selectedArray.length === 0) {
      alert(
        'Select at least one SKU first.'
      );

      return;
    }

    if (totalQty <= 0) {
      alert(
        'Total quantity must be greater than 0.'
      );

      return;
    }

    if (
      !totalCost ||
      totalCostNumber <= 0
    ) {
      alert(
        'Enter the total amount you paid.'
      );

      return;
    }

    const defaultName =
      notes.trim() ||
      buildDefaultPurchaseName();

    const name = window.prompt(
      'Name this purchase / FIFO batch note:',
      defaultName
    );

    if (!name || !name.trim()) {
      alert(
        'Purchase name is required.'
      );

      return;
    }

    setCommitNoteDraft(name.trim());

    const releaseNow = window.confirm(
      'Would you like to release these FIFO batches now?\n\n' +
        'OK = Release now and add the quantities into inventory\n' +
        'Cancel = Keep the batches pending for later'
    );

    const platformPreview = PLATFORMS.map(
      (platform) => {
        const total =
          platformTotals[platform];

        return `${platform.toUpperCase()}: Profit ${formatMoney(
          total?.totalProfit || 0
        )} | Margin ${Number(
          total?.marginPercent || 0
        ).toFixed(1)}% | ROI ${Number(
          total?.roiPercent || 0
        ).toFixed(1)}%`;
      }
    ).join('\n');

    const previewLines = selectedArray
      .map((item) => {
        const weighted =
          weightedCostBySku[item.sku];

        const itemUnitCost =
          weighted?.unitCost || 0;

        const lineTotal =
          weighted?.assignedTotal || 0;

        const temporaryLabel =
          weighted?.isTemporaryPrice
            ? ' temp sell'
            : '';

        return `${item.label}: ${
          item.qty
        } @ ${formatMoney(
          itemUnitCost,
          4
        )} = ${formatMoney(
          lineTotal
        )}${temporaryLabel}`;
      })
      .join('\n');

    const confirmCreate = window.confirm(
      `${
        releaseNow
          ? 'Create and Release'
          : 'Create Pending'
      } Weighted Batches?\n\n` +
        `Name: ${name.trim()}\n` +
        `FIFO action: ${
          releaseNow
            ? 'Release immediately into inventory'
            : 'Keep pending for later'
        }\n\n` +
        `Total paid: ${formatMoney(
          totalCostNumber
        )}\n` +
        `Total qty: ${totalQty}\n` +
        `Target margin: 40%\n` +
        `Avg cost: ${formatMoney(
          averageUnitCost,
          4
        )}\n` +
        `Shipping cost: ${formatMoney(
          shippingCostNumber
        )}\n` +
        `Packaging cost: ${formatMoney(
          packagingCost
        )}\n\n` +
        `Estimated Profit by Platform:\n${platformPreview}\n\n` +
        previewLines
    );

    if (!confirmCreate) return;

    await commitQuickBuy(
      name.trim(),
      releaseNow
    );
  };

  const commitQuickBuy = async (
    finalNote,
    releaseNow = false
  ) => {
    try {
      setCommitting(true);

      const payload = {
        totalCost: totalCostNumber,
        notes: finalNote,
        purchaseName: finalNote,
        splitMode:
          'weighted_40_margin',
        targetMargin: TARGET_MARGIN,

        items: selectedArray.map(
          (item) => {
            const weighted =
              weightedCostBySku[item.sku];

            const product =
              productsBySku[item.sku];

            return {
              sku: item.sku,
              qty: item.qty,
              sellPriceOverride:
                weighted?.sellPrice ||
                Number(
                  product?.price || 0
                ),
            };
          }
        ),
      };

      const response = await api.post(
        '/quick-batch/commit',
        payload
      );

      const createdBatches =
        Array.isArray(
          response.data?.createdBatches
        )
          ? response.data.createdBatches
          : [];

      const createdCount =
        Number(
          response.data?.createdCount
        ) ||
        createdBatches.length ||
        selectedArray.length;

      if (!releaseNow) {
        alert(
          `${createdCount} weighted FIFO ${
            createdCount === 1
              ? 'batch was'
              : 'batches were'
          } created and left pending for later.`
        );

        clearCart();
        await loadSkus();
        return;
      }

      if (createdBatches.length === 0) {
        alert(
          `${createdCount} pending ${
            createdCount === 1
              ? 'batch was'
              : 'batches were'
          } created, but no batch IDs were returned.\n\n` +
            'They were not released and remain pending for later.'
        );

        clearCart();
        await loadSkus();
        return;
      }

      const releaseResults =
        await Promise.allSettled(
          createdBatches.map((batch) =>
            api.post(
              `/products/fifo/batches/${batch.batchId}/release`
            )
          )
        );

      const releasedBatches = [];
      const failedBatches = [];

      releaseResults.forEach(
        (result, index) => {
          const batch =
            createdBatches[index];

          if (
            result.status ===
            'fulfilled'
          ) {
            releasedBatches.push(batch);
          } else {
            failedBatches.push({
              ...batch,
              error:
                result.reason?.response
                  ?.data?.error ||
                result.reason?.response
                  ?.data?.details ||
                result.reason?.message ||
                'Unknown release error',
            });
          }
        }
      );

      if (failedBatches.length === 0) {
        alert(
          `${
            releasedBatches.length
          } weighted FIFO ${
            releasedBatches.length === 1
              ? 'batch was created and released'
              : 'batches were created and released'
          } into inventory.`
        );

        clearCart();
        await loadSkus();
        return;
      }

      const failedList =
        failedBatches
          .map(
            (batch) =>
              `${
                batch.sku ||
                `Batch ${batch.batchId}`
              }: ${batch.error}`
          )
          .join('\n');

      alert(
        `${createdCount} ${
          createdCount === 1
            ? 'batch was'
            : 'batches were'
        } created.\n\n` +
          `Released successfully: ${releasedBatches.length}\n` +
          `Still pending because release failed: ${failedBatches.length}\n\n` +
          `Failed releases:\n${failedList}`
      );

      clearCart();
      await loadSkus();
    } catch (err) {
      console.error(
        '[QuickBuy Web] commit error:',
        err.response?.data || err.message
      );

      alert(
        err.response?.data?.details ||
          err.response?.data?.error ||
          err.message ||
          'Commit failed.'
      );
    } finally {
      setCommitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <h2>Quick Buy</h2>
        <p>
          Loading Quick Buy SKUs...
        </p>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2>Quick Buy</h2>

          <p className="text-muted mb-0">
            Weighted split by sell price.
            Temporary sell price is only
            used for Quick Buy math.
          </p>
        </div>

        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleImportFromShopify}
            disabled={importingShopify}
          >
            {importingShopify
              ? 'Importing...'
              : 'Import from Shopify'}
          </button>

          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={clearCart}
            disabled={importingShopify}
          >
            Clear
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}{' '}

          <button
            className="btn btn-sm btn-light ms-2"
            onClick={loadSkus}
          >
            Retry
          </button>
        </div>
      )}

      {profitSettingsError && (
        <div className="alert alert-warning">
          Profit calculator settings did
          not load: {profitSettingsError}{' '}

          <button
            className="btn btn-sm btn-light ms-2"
            onClick={loadProfitSettings}
          >
            Retry
          </button>
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-3">
          <div className="card p-3">
            <strong>
              {selectedArray.length}
            </strong>
            <span>SKUs</span>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card p-3">
            <strong>{totalQty}</strong>
            <span>Units</span>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card p-3">
            <strong>
              {formatMoney(
                totalCostNumber
              )}
            </strong>
            <span>Paid</span>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card p-3">
            <strong>
              {formatMoney(
                averageUnitCost
              )}
            </strong>
            <span>Average Cost</span>
          </div>
        </div>
      </div>

      {selectedArray.length > 0 &&
        profitSettings && (
          <div className="card p-3 mb-3">
            <h5>
              Net Profit by Platform
            </h5>

            <div className="row">
              {PLATFORMS.map(
                (platform) => {
                  const total =
                    platformTotals[
                      platform
                    ];

                  return (
                    <div
                      className="col-md-3 mb-2"
                      key={platform}
                    >
                      <div className="border rounded p-2 h-100">
                        <strong>
                          {platform.toUpperCase()}
                        </strong>

                        <div
                          className={
                            total?.totalProfit >=
                            0
                              ? 'text-success'
                              : 'text-danger'
                          }
                        >
                          Profit:{' '}
                          {formatMoney(
                            total?.totalProfit ||
                              0
                          )}
                        </div>

                        <small className="d-block">
                          Margin:{' '}
                          {Number(
                            total?.marginPercent ||
                              0
                          ).toFixed(1)}
                          %
                        </small>

                        <small className="d-block">
                          ROI:{' '}
                          {Number(
                            total?.roiPercent ||
                              0
                          ).toFixed(1)}
                          %
                        </small>

                        <small className="text-muted d-block">
                          Fees/Ship/Pack:{' '}
                          {formatMoney(
                            total?.totalFees ||
                              0
                          )}
                        </small>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        )}

      <div className="row">
        <div className="col-lg-4 mb-3">
          <div className="card p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">
                Vendor Cost
              </h5>

              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() =>
                  setShowProfitSettingsEditor(
                    (previous) =>
                      !previous
                  )
                }
              >
                {showProfitSettingsEditor
                  ? 'Hide Settings'
                  : 'Profit Settings'}
              </button>
            </div>

            <input
              className="form-control mb-2"
              value={totalCost}
              onChange={(event) =>
                setTotalCost(
                  event.target.value
                )
              }
              placeholder="Total paid, example 100.00"
            />

            <input
              className="form-control mb-2"
              value={shippingCost}
              onChange={(event) =>
                setShippingCost(
                  event.target.value
                )
              }
              placeholder="Shipping cost, example 4.50"
            />

            <textarea
              className="form-control mb-2"
              value={notes}
              onChange={(event) =>
                setNotes(
                  event.target.value
                )
              }
              placeholder="Optional note before commit"
              rows={3}
            />

            {profitSettings && (
              <div className="border rounded p-2 mb-2">
                <h6 className="mb-2">
                  Selected Profit Settings
                </h6>

                <div>
                  <strong>
                    Selected Packaging:
                  </strong>
                </div>

                {selectedPackagingProfiles.length ? (
                  selectedPackagingProfiles.map(
                    (profile) => (
                      <div key={profile.id}>
                        <small>
                          {profile.name ||
                            `Packaging ${profile.id}`}{' '}
                          —{' '}
                          {formatMoney(
                            profile.cost
                          )}
                        </small>
                      </div>
                    )
                  )
                ) : (
                  <small className="text-muted d-block">
                    No selected packaging
                    profiles.
                  </small>
                )}

                <small className="text-muted d-block mt-2">
                  Packaging total:{' '}
                  {formatMoney(
                    packagingCost
                  )}
                </small>
              </div>
            )}

            {showProfitSettingsEditor &&
              profitSettings &&
              profitSettingsDraft && (
                <div className="border rounded p-3 mb-3">
                  <h5>
                    Profit Calculator Settings
                  </h5>

                  <h6 className="mt-3">
                    Packaging Profiles
                  </h6>

                  {(
                    profitSettings.packaging_profiles ||
                    []
                  ).map((profile) => (
                    <div
                      className="row g-2 align-items-center mb-2"
                      key={profile.id}
                    >
                      <div className="col-auto">
                        <input
                          type="checkbox"
                          checked={
                            !!profile.selected
                          }
                          onChange={(
                            event
                          ) =>
                            updatePackagingProfile(
                              profile.id,
                              'selected',
                              event.target
                                .checked
                            )
                          }
                        />
                      </div>

                      <div className="col">
                        <input
                          className="form-control"
                          value={
                            profile.name ||
                            ''
                          }
                          onChange={(
                            event
                          ) =>
                            updatePackagingProfile(
                              profile.id,
                              'name',
                              event.target
                                .value
                            )
                          }
                          placeholder="Packaging name"
                        />
                      </div>

                      <div className="col-4">
                        <input
                          className="form-control"
                          value={
                            profile.cost ??
                            ''
                          }
                          onChange={(
                            event
                          ) =>
                            updatePackagingProfile(
                              profile.id,
                              'cost',
                              event.target
                                .value
                            )
                          }
                          placeholder="Cost"
                        />
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary mb-3"
                    onClick={
                      addPackagingProfile
                    }
                  >
                    + Add Packaging
                  </button>

                  <h6>
                    Platform Marketplace Fees
                  </h6>

                  {settingsInput(
                    'ebay_fee_percent',
                    'eBay Marketplace Fee %'
                  )}

                  {settingsInput(
                    'shopify_fee_percent',
                    'Shopify Marketplace Fee %'
                  )}

                  {settingsInput(
                    'tiktok_fee_percent',
                    'TikTok Marketplace Fee %'
                  )}

                  {settingsInput(
                    'whatnot_fee_percent',
                    'Whatnot Marketplace Fee %'
                  )}

                  <h6 className="mt-3">
                    Payment Processing — eBay
                  </h6>

                  {settingsInput(
                    'ebay_payment_percent',
                    'eBay Payment Fee %'
                  )}

                  {settingsInput(
                    'ebay_payment_flat',
                    'eBay Payment Flat Fee'
                  )}

                  <h6 className="mt-3">
                    Payment Processing — Shopify
                  </h6>

                  {settingsInput(
                    'shopify_payment_percent',
                    'Shopify Payment Fee %'
                  )}

                  {settingsInput(
                    'shopify_payment_flat',
                    'Shopify Payment Flat Fee'
                  )}

                  <h6 className="mt-3">
                    Payment Processing — TikTok
                  </h6>

                  {settingsInput(
                    'tiktok_payment_percent',
                    'TikTok Payment Fee %'
                  )}

                  {settingsInput(
                    'tiktok_payment_flat',
                    'TikTok Payment Flat Fee'
                  )}

                  <h6 className="mt-3">
                    Payment Processing — Whatnot
                  </h6>

                  {settingsInput(
                    'whatnot_payment_percent',
                    'Whatnot Payment Fee %'
                  )}

                  {settingsInput(
                    'whatnot_payment_flat',
                    'Whatnot Payment Flat Fee'
                  )}

                  <button
                    type="button"
                    className="btn btn-primary w-100 mt-2"
                    onClick={
                      saveProfitSettings
                    }
                    disabled={
                      savingProfitSettings
                    }
                  >
                    {savingProfitSettings
                      ? 'Saving...'
                      : 'Save Settings'}
                  </button>
                </div>
              )}

            <button
              className="btn btn-warning w-100"
              disabled={
                committing ||
                selectedArray.length === 0
              }
              onClick={openCommit}
            >
              {committing
                ? 'Creating...'
                : `Name Purchase & Create · ${selectedArray.length} SKUs / ${totalQty} Units`}
            </button>
          </div>

          <div className="card p-3">
            <h5>
              Weighted Split Preview
            </h5>

            <p className="text-muted">
              Target margin: 40%
            </p>

            {selectedArray.length === 0 ? (
              <p>
                No SKUs selected yet.
              </p>
            ) : (
              selectedArray.map((item) => {
                const weighted =
                  weightedCostBySku[
                    item.sku
                  ];

                const itemUnitCost =
                  weighted?.unitCost || 0;

                const lineTotal =
                  weighted?.assignedTotal ||
                  0;

                const sellPrice =
                  weighted?.sellPrice || 0;

                const tempLabel =
                  weighted?.isTemporaryPrice
                    ? ' TEMP'
                    : '';

                return (
                  <div
                    key={item.sku}
                    className="border-bottom py-2"
                  >
                    <strong>
                      {item.label}
                    </strong>

                    <div>
                      {item.qty} ×{' '}
                      {formatMoney(
                        itemUnitCost,
                        4
                      )}{' '}
                      ={' '}
                      {formatMoney(
                        lineTotal
                      )}
                    </div>

                    <small>
                      Sell{' '}
                      {formatMoney(
                        sellPrice
                      )}
                      {tempLabel}
                    </small>

                    {profitSettings && (
                      <div className="mt-2">
                        {PLATFORMS.map(
                          (platform) => {
                            const roi =
                              roiBySku[
                                item.sku
                              ]?.[
                                platform
                              ];

                            if (!roi) {
                              return null;
                            }

                            return (
                              <small
                                key={
                                  platform
                                }
                                className="d-block"
                              >
                                <strong>
                                  {platform.toUpperCase()}
                                  :
                                </strong>{' '}
                                <span
                                  className={
                                    roi.profit >=
                                    0
                                      ? 'text-success'
                                      : 'text-danger'
                                  }
                                >
                                  {formatMoney(
                                    roi.profit
                                  )}
                                </span>{' '}
                                · Margin{' '}
                                {Number(
                                  roi.marginPercent ||
                                    0
                                ).toFixed(
                                  1
                                )}
                                % · ROI{' '}
                                {Number(
                                  roi.roiPercent ||
                                    0
                                ).toFixed(
                                  1
                                )}
                                %
                              </small>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="col-lg-8">
          <div className="card p-3 mb-3">
            <div className="mb-3">
              <label className="form-label fw-bold">
                Add SKU to Quick Buy
              </label>

              <div className="d-flex gap-2">
                <input
                  className="form-control"
                  value={quickAddSku}
                  onChange={(event) =>
                    setQuickAddSku(
                      event.target.value
                    )
                  }
                  placeholder="Type existing SKU, example M-B-U-8089"
                  onKeyDown={(event) => {
                    if (
                      event.key === 'Enter'
                    ) {
                      addSkuToQuickBuy();
                    }
                  }}
                />

                <button
                  className="btn btn-success"
                  onClick={
                    addSkuToQuickBuy
                  }
                  disabled={
                    addingQuickSku
                  }
                >
                  {addingQuickSku
                    ? 'Adding...'
                    : 'Add SKU'}
                </button>
              </div>

              <small className="text-muted">
                This does not create a
                product. It only adds an
                existing product SKU to
                the Quick Buy list.
              </small>
            </div>

            <input
              className="form-control mb-2"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search product or SKU..."
            />

            <div className="d-flex gap-2">
              <button
                className={`btn ${
                  !showOnlySelected
                    ? 'btn-primary'
                    : 'btn-outline-primary'
                }`}
                onClick={() =>
                  setShowOnlySelected(
                    false
                  )
                }
              >
                All Products
              </button>

              <button
                className={`btn ${
                  showOnlySelected
                    ? 'btn-primary'
                    : 'btn-outline-primary'
                }`}
                onClick={() =>
                  setShowOnlySelected(
                    true
                  )
                }
              >
                Selected (
                {selectedArray.length})
              </button>

              <button
                className="btn btn-outline-secondary"
                onClick={loadSkus}
              >
                Refresh
              </button>
            </div>
          </div>

          <h5>
            Products —{' '}
            {displaySkus.length} shown
          </h5>

          {displaySkus.length === 0 ? (
            <div className="alert alert-secondary">
              No matching products found.
            </div>
          ) : (
            <div className="row">
              {displaySkus.map((item) => {
                const selected =
                  !!selectedItems[
                    item.sku
                  ];

                const selectedItem =
                  selectedItems[
                    item.sku
                  ];

                const selectedQty =
                  selectedItem?.qty || 0;

                const imageUrl =
                  getImageUrl(
                    item.image
                  );

                const normalSellPrice =
                  Number(
                    item.price || 0
                  );

                const effectiveSellPrice =
                  selected
                    ? getEffectiveSellPrice(
                        item.sku
                      )
                    : normalSellPrice;

                const weighted =
                  weightedCostBySku[
                    item.sku
                  ];

                const itemUnitCost =
                  weighted?.unitCost || 0;

                const lineTotal =
                  weighted?.assignedTotal ||
                  0;

                const targetUnitCost =
                  weighted?.targetUnitCost ||
                  (effectiveSellPrice > 0
                    ? effectiveSellPrice *
                      TARGET_COST_RATE
                    : 0);

                const hasTempPrice =
                  selected &&
                  parseMoneyNumber(
                    selectedItem?.tempSellPrice
                  ) >
                    0 &&
                  parseMoneyNumber(
                    selectedItem?.tempSellPrice
                  ) !==
                    normalSellPrice;

                return (
                  <div
                    className="col-md-6 col-xl-4 mb-3"
                    key={item.sku}
                  >
                    <div
                      className={`card h-100 ${
                        selected
                          ? 'border-warning'
                          : Number(
                              item.is_pinned ||
                                0
                            )
                          ? 'border-primary'
                          : ''
                      }`}
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <span
                            className={
                              Number(
                                item.is_pinned ||
                                  0
                              )
                                ? 'badge bg-primary'
                                : 'badge bg-light text-dark'
                            }
                          >
                            {Number(
                              item.is_pinned ||
                                0
                            )
                              ? 'Pinned'
                              : 'Not Pinned'}
                          </span>

                          <button
                            type="button"
                            className={`btn btn-sm ${
                              Number(
                                item.is_pinned ||
                                  0
                              )
                                ? 'btn-primary'
                                : 'btn-outline-secondary'
                            }`}
                            onClick={() =>
                              togglePinnedSku(
                                item
                              )
                            }
                          >
                            {Number(
                              item.is_pinned ||
                                0
                            )
                              ? 'Unpin'
                              : 'Pin'}
                          </button>
                        </div>

                        {imageUrl && (
                          <img
                            src={imageUrl}
                            alt={
                              item.label ||
                              item.name ||
                              item.sku
                            }
                            style={{
                              width: '100%',
                              height: 140,
                              objectFit:
                                'cover',
                              borderRadius: 8,
                              marginBottom: 10,
                            }}
                          />
                        )}

                        <h6>
                          {item.label ||
                            item.name ||
                            item.sku}
                        </h6>

                        <small className="text-muted d-block mb-2">
                          {item.sku}
                        </small>

                        <div className="mb-2">
                          <div>
                            Stock:{' '}
                            {item.stock ??
                              0}
                          </div>

                          <div>
                            {hasTempPrice
                              ? 'Temp Sell'
                              : 'Sell'}
                            :{' '}
                            {formatMoney(
                              effectiveSellPrice
                            )}
                          </div>

                          <div>
                            40% Target:{' '}
                            {formatMoney(
                              targetUnitCost
                            )}
                          </div>
                        </div>

                        <button
                          className="btn btn-sm btn-success w-100 mb-2"
                          onClick={() =>
                            addOrIncreaseSku(
                              item
                            )
                          }
                        >
                          +1
                        </button>

                        {selected && (
                          <div className="border-top pt-2">
                            <label className="form-label">
                              Buying Qty
                            </label>

                            <div className="d-flex gap-2 mb-2">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() =>
                                  updateQty(
                                    item.sku,
                                    selectedQty -
                                      1
                                  )
                                }
                              >
                                -
                              </button>

                              <input
                                className="form-control form-control-sm text-center"
                                value={
                                  selectedQty
                                }
                                onChange={(
                                  event
                                ) =>
                                  updateQty(
                                    item.sku,
                                    event
                                      .target
                                      .value
                                  )
                                }
                              />

                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() =>
                                  updateQty(
                                    item.sku,
                                    selectedQty +
                                      1
                                  )
                                }
                              >
                                +
                              </button>

                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() =>
                                  updateQty(
                                    item.sku,
                                    selectedQty +
                                      5
                                  )
                                }
                              >
                                +5
                              </button>
                            </div>

                            <label className="form-label">
                              What-if sell price
                            </label>

                            <input
                              className="form-control form-control-sm mb-2"
                              value={
                                selectedItem?.tempSellPrice ||
                                ''
                              }
                              onChange={(
                                event
                              ) =>
                                updateTempSellPrice(
                                  item.sku,
                                  event.target
                                    .value
                                )
                              }
                              placeholder={
                                normalSellPrice >
                                0
                                  ? normalSellPrice.toFixed(
                                      2
                                    )
                                  : '0.00'
                              }
                            />

                            <small className="text-muted d-block mb-2">
                              This does not change
                              product price.
                            </small>

                            {profitSettings && (
                              <div className="border rounded p-2 mb-2">
                                {PLATFORMS.map(
                                  (
                                    platform
                                  ) => {
                                    const roi =
                                      roiBySku[
                                        item
                                          .sku
                                      ]?.[
                                        platform
                                      ];

                                    if (
                                      !roi
                                    ) {
                                      return null;
                                    }

                                    return (
                                      <small
                                        key={
                                          platform
                                        }
                                        className="d-block"
                                      >
                                        <strong>
                                          {platform.toUpperCase()}
                                          :
                                        </strong>{' '}
                                        <span
                                          className={
                                            roi.profit >=
                                            0
                                              ? 'text-success'
                                              : 'text-danger'
                                          }
                                        >
                                          {formatMoney(
                                            roi.profit
                                          )}
                                        </span>{' '}
                                        · ROI{' '}
                                        {Number(
                                          roi.roiPercent ||
                                            0
                                        ).toFixed(
                                          1
                                        )}
                                        %
                                      </small>
                                    );
                                  }
                                )}
                              </div>
                            )}

                            {hasTempPrice && (
                              <button
                                className="btn btn-sm btn-outline-danger me-2"
                                onClick={() =>
                                  resetTempSellPrice(
                                    item.sku
                                  )
                                }
                              >
                                Reset Temp Price
                              </button>
                            )}

                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                removeSku(
                                  item.sku
                                )
                              }
                            >
                              Remove
                            </button>

                            <div className="mt-2">
                              <small>
                                {itemUnitCost >
                                0
                                  ? `${formatMoney(
                                      lineTotal
                                    )} assigned · ${formatMoney(
                                      itemUnitCost,
                                      4
                                    )} each`
                                  : `Target buy cost: ${formatMoney(
                                      targetUnitCost
                                    )} each`}
                              </small>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}